import React, { useCallback, useMemo, useRef, useState } from "react";
import { Calendar, ChevronDown, Download, Plus, Search } from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { financeJournalAPI } from "../../../services";
import { useDebounce } from "../../../hooks/useDebounce";
import "../../../style/financialOverview.css";
import "../../../style/journalView.css";
import FilePdf from "../../../assets/file-pdf.svg";
import FileExcel from "../../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../../assets/file-csv.svg";
import TotalSaleIcon from "../../../assets/total-sale-icon.svg";
import TotalCreditIcon from "../../../assets/total-credit-icon.svg";
import TotalDebitIcon from "../../../assets/total-debit-icon.svg";
import BalanceIcon from "../../../assets/balance-icon.svg";
import EditIcon from "../../../assets/icons/EditIcon";
import RedDeleteIcon from "../../../assets/icons/RedDeleteIcon";
import AddJournalModal from "./components/AddJournalModal";
import EditJournalModal from "./components/EditJournalModal";
import DeleteTransactionModal from "./components/DeleteTransactionModal";
import FinancialDateRangePicker, { formatRangeLabel } from "./components/FinancialDateRangePicker";
import MaskedCurrencyValue from "../../common/MaskedCurrencyValue";

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => {
  const number = typeof value === "number" ? value : parseFloat(value);
  return currencyFormatter.format(Number.isFinite(number) ? number : 0);
};

const ITEMS_PER_PAGE = 10;

const parseEntryDate = (value) => {
  if (!value) return new Date();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const mapCashboxEntry = (entry, index) => {
  const tnxType = (entry.tnx_type || "").toLowerCase();
  const cashboxType = (entry.cashbox_type || "").toUpperCase();
  const isDeposit =
    cashboxType === "DEPOSIT" || tnxType === "debit";

  return {
    id: entry.id ?? entry.tnx_id ?? `cashbox-${index}`,
    apiId: entry.id,
    date: parseEntryDate(entry.created_at || entry.tnx_date),
    transactionId: entry.tnx_id || String(entry.id) || "—",
    note: entry.note?.trim() || entry.title || "—",
    performedBy: entry.creator_name || "—",
    amount: parseFloat(entry.amount) || 0,
    transactionType: isDeposit ? "Deposit" : "Withdraw",
    tnx_type: tnxType,
    cashbox_type: cashboxType,
    tnx_date: entry.tnx_date || null,
    originalData: entry,
  };
};

const getCashboxEntryId = (transaction) =>
  transaction?.apiId ??
  transaction?.originalData?.id ??
  transaction?.id;

const extractApiErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.detail ||
  (Array.isArray(error.response?.data?.non_field_errors)
    ? error.response.data.non_field_errors[0]
    : null) ||
  error.message ||
  "Request failed";

const parseCashboxResponse = (response) => {
  const payload = response?.data ?? response;
  const nested = payload?.data ?? payload;

  const results = Array.isArray(nested?.results)
    ? nested.results
    : Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload)
        ? payload
        : [];

  return {
    results,
    summary: nested?.summary ?? payload?.summary ?? null,
    count: nested?.count ?? payload?.count ?? results.length,
    next: nested?.next ?? payload?.next ?? null,
  };
};

const formatDateForAPI = (date) => {
  const d = date || new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  monthStart.setHours(0, 0, 0, 0);

  return {
    from: formatDateForAPI(monthStart),
    to: formatDateForAPI(today),
    startDate: monthStart,
    endDate: today,
  };
};

const formatTransactionDateForExport = (date) =>
  date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const EXPORT_FILE_BASE = "profit-management-transactions";

function FinancialOverview() {
  const initialDateRange = useMemo(() => getDefaultDateRange(), []);
  const [searchTerm, setSearchTerm] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [cashboxSummary, setCashboxSummary] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showAddJournalModal, setShowAddJournalModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(initialDateRange.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(initialDateRange.endDate);
  const [appliedDateRange, setAppliedDateRange] = useState({
    from: initialDateRange.from,
    to: initialDateRange.to,
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const exportDropdownRef = useRef(null);
  const datePickerRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  const fetchCashboxEntries = useCallback(async () => {
      setIsLoading(true);
      try {
        const params = {
          type: typeFilter,
          page: currentPage,
          page_size: ITEMS_PER_PAGE,
        };
        if (appliedDateRange?.from && appliedDateRange?.to) {
          params.from = appliedDateRange.from;
          params.to = appliedDateRange.to;
        }
        if (debouncedSearchTerm.trim().length > 0) {
          params.search = debouncedSearchTerm.trim();
        }

        const response = await financeJournalAPI.getCashboxJournals(params);
        const { results, summary, count, next } = parseCashboxResponse(response);
        setTransactions(results.map(mapCashboxEntry));
        setCashboxSummary(summary);
        setTotalCount(count);
        setHasNextPage(Boolean(next));
      } catch (error) {
        console.error("Error fetching cashbox entries:", error);
        toast.error("Failed to load transactions");
        setTransactions([]);
        setCashboxSummary(null);
        setTotalCount(0);
        setHasNextPage(false);
      } finally {
        setIsLoading(false);
      }
    },
    [typeFilter, appliedDateRange, debouncedSearchTerm, currentPage]
  );

  React.useEffect(() => {
    fetchCashboxEntries();
  }, [fetchCashboxEntries]);

  const overviewMetrics = useMemo(() => {
    if (cashboxSummary) {
      return {
        salesProfit: parseFloat(cashboxSummary.sales_profit) || 0,
        totalDeposits: parseFloat(cashboxSummary.total_deposits) || 0,
        totalWithdraws: parseFloat(cashboxSummary.total_withdraws) || 0,
        balance: parseFloat(cashboxSummary.balance) || 0,
        totalEntries: cashboxSummary.total_entries ?? totalCount,
      };
    }

    const totalDeposits = transactions
      .filter((t) => t.transactionType === "Deposit")
      .reduce((sum, t) => sum + t.amount, 0);
    const totalWithdraws = transactions
      .filter((t) => t.transactionType === "Withdraw")
      .reduce((sum, t) => sum + t.amount, 0);
    return {
      salesProfit: 0,
      totalDeposits,
      totalWithdraws,
      balance: totalDeposits - totalWithdraws,
      totalEntries: transactions.length,
    };
  }, [transactions, cashboxSummary, totalCount]);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, appliedDateRange, debouncedSearchTerm]);

  const totalPages = useMemo(() => {
    if (totalCount > ITEMS_PER_PAGE) {
      return Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
    }
    if (hasNextPage) {
      return Math.max(currentPage + 1, 2);
    }
    return Math.max(1, currentPage);
  }, [totalCount, hasNextPage, currentPage]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (updatedData) => {
    const journalId = getCashboxEntryId(editingTransaction);
    if (!journalId) {
      toast.error("Cannot update: missing transaction id");
      throw new Error("Missing journal id");
    }

    try {
      await financeJournalAPI.patchCashboxJournal(journalId, {
        amount: updatedData.amount,
        note: updatedData.note,
        tnx_date: updatedData.tnx_date || formatDateForAPI(new Date()),
      });
      toast.success("Journal entry updated successfully");
      setShowEditModal(false);
      setEditingTransaction(null);
      await fetchCashboxEntries();
    } catch (error) {
      console.error("Error updating cashbox journal:", error);
      toast.error(extractApiErrorMessage(error));
      throw error;
    }
  };

  const handleDeleteTransaction = (transaction) => {
    setDeletingTransaction(transaction);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (transaction) => {
    const journalId = getCashboxEntryId(transaction);
    if (!journalId) {
      toast.error("Cannot delete: missing transaction id");
      throw new Error("Missing journal id");
    }

    try {
      await financeJournalAPI.deleteCashboxJournal(journalId);
      toast.success("Transaction deleted successfully");
      setShowDeleteModal(false);
      setDeletingTransaction(null);
      await fetchCashboxEntries();
    } catch (error) {
      console.error("Error deleting cashbox journal:", error);
      toast.error(extractApiErrorMessage(error));
      throw error;
    }
  };

  const handleAddJournal = async ({ transactionType, amount, note, tnx_date }) => {
    const tnx_type = transactionType === "Deposit" ? "debit" : "credit";

    try {
      await financeJournalAPI.createCashboxJournal({
        tnx_type,
        amount,
        tnx_date: tnx_date || formatDateForAPI(new Date()),
        note,
      });
      toast.success("Journal entry added successfully");
      await fetchCashboxEntries();
    } catch (error) {
      console.error("Error adding cashbox journal:", error);
      toast.error(extractApiErrorMessage(error) || "Failed to add journal entry");
      throw error;
    }
  };

  const handleDateClick = (date) => {
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
      return;
    }

    if (date < selectedStartDate) {
      setSelectedEndDate(selectedStartDate);
      setSelectedStartDate(date);
    } else {
      setSelectedEndDate(date);
    }
  };

  const navigateMonth = (direction) => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(nextMonth);
  };

  const handleApplyDatePicker = () => {
    if (selectedStartDate && selectedEndDate) {
      setAppliedDateRange({
        from: formatDateForAPI(selectedStartDate),
        to: formatDateForAPI(selectedEndDate),
      });
      setShowDatePicker(false);
      setCurrentPage(1);
    }
  };

  const handleClearDatePicker = () => {
    const defaultRange = getDefaultDateRange();
    setSelectedStartDate(defaultRange.startDate);
    setSelectedEndDate(defaultRange.endDate);
    setAppliedDateRange({
      from: defaultRange.from,
      to: defaultRange.to,
    });
    setCurrentMonth(new Date());
    setCurrentPage(1);
  };

  const getExportTransactions = useCallback(
    () => transactions,
    [transactions]
  );

  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "excel", label: "Excel", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const mapTransactionToExportRow = (transaction) => ({
    Date: formatTransactionDateForExport(transaction.date),
    "TRN ID": transaction.transactionId,
    Note: transaction.note,
    "Performed By": transaction.performedBy,
    Type: transaction.transactionType,
    Amount: transaction.amount,
  });

  const handleExportSelect = (value) => {
    setShowExportDropdown(false);

    const exportTransactions = getExportTransactions();
    if (exportTransactions.length === 0) {
      toast.info("No transactions to export");
      return;
    }

    switch (value) {
      case "pdf":
        exportToPDF(exportTransactions);
        break;
      case "excel":
        exportToExcel(exportTransactions);
        break;
      case "csv":
        exportToCSV(exportTransactions);
        break;
      default:
        break;
    }
  };

  const exportToExcel = (exportTransactions) => {
    const excelData = exportTransactions.map(mapTransactionToExportRow);
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");

    ws["!cols"] = [
      { wch: 22 },
      { wch: 15 },
      { wch: 30 },
      { wch: 20 },
      { wch: 12 },
      { wch: 12 },
    ];

    XLSX.writeFile(wb, `${EXPORT_FILE_BASE}.xlsx`);
  };

  const exportToCSV = (exportTransactions) => {
    const csvHeaders = [
      "Date",
      "TRN ID",
      "Note",
      "Performed By",
      "Type",
      "Amount",
    ];
    const csvData = exportTransactions.map((transaction) => {
      const row = mapTransactionToExportRow(transaction);
      return [
        row.Date,
        row["TRN ID"],
        row.Note,
        row["Performed By"],
        row.Type,
        row.Amount,
      ];
    });

    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${EXPORT_FILE_BASE}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToPDF = (exportTransactions) => {
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Profit Management", 14, 22);

    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString("en-GB")}`, 14, 30);

    if (appliedDateRange?.from && appliedDateRange?.to) {
      doc.text(
        `Date range: ${appliedDateRange.from} - ${appliedDateRange.to}`,
        14,
        36
      );
    }

    const tableData = exportTransactions.map((transaction) => {
      const row = mapTransactionToExportRow(transaction);
      return [
        row.Date,
        row["TRN ID"],
        row.Note,
        row["Performed By"],
        row.Type,
        `€${Number(row.Amount).toFixed(2)}`,
      ];
    });

    autoTable(doc, {
      head: [["Date", "TRN ID", "Note", "Performed By", "Type", "Amount"]],
      body: tableData,
      startY: appliedDateRange?.from && appliedDateRange?.to ? 42 : 40,
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [246, 248, 250],
        textColor: [87, 96, 106],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      columnStyles: {
        5: { halign: "right" },
      },
    });

    doc.save(`${EXPORT_FILE_BASE}.pdf`);
  };

  const stats = [
    {
      label: "Sales Profit",
      value: formatCurrency(overviewMetrics.salesProfit),
      accent: "overview-stat-sales-profit",
      icon: TotalSaleIcon,
    },
    {
      label: "Total Deposits",
      value: formatCurrency(overviewMetrics.totalDeposits),
      accent: "overview-stat-deposits",
      icon: TotalDebitIcon,
    },
    {
      label: "Total Withdraws",
      value: formatCurrency(overviewMetrics.totalWithdraws),
      accent: "overview-stat-withdraws",
      icon: TotalCreditIcon,
    },
    {
      label: "Balance",
      value: formatCurrency(overviewMetrics.balance),
      accent: "overview-stat-balance",
      icon: BalanceIcon,
    },
  ];

  return (
    <div className="financial-overview-page">
      <section className="financial-overview-shell">
        <div className="financial-overview-card">
          <div className="financial-overview-card-header">
            <div>
              <h1>Profit Management</h1>
              <p>Track your daily profit and cashbox transaction activity in one place.</p>
            </div>
            <div className="financial-overview-actions">
              <button
                type="button"
                className="financial-overview-date-button"
                onClick={() => setShowDatePicker((value) => !value)}
              >
                <Calendar size={16} />
                <span>
                  {appliedDateRange
                    ? `${appliedDateRange.from} - ${appliedDateRange.to}`
                    : formatRangeLabel(selectedStartDate, selectedEndDate)}
                </span>
              </button>
              <button type="button" className="financial-overview-primary-button" onClick={() => setShowAddJournalModal(true)}>
                <Plus size={16} />
                Add Journal
              </button>
            </div>
          </div>

          <div className="financial-overview-stats">
            {stats.map((stat) => (
              <article key={stat.label} className={`financial-overview-stat ${stat.accent}`}>
                <div>
                  <p>{stat.label}</p>
                  <strong>
                    <MaskedCurrencyValue value={stat.value} />
                  </strong>
                </div>
                <div className="financial-overview-stat-icon-wrap">
                  <img src={stat.icon} alt={stat.label} className="financial-overview-stat-icon" />
                </div>
              </article>
            ))}
          </div>

          {showAddJournalModal && (
            <AddJournalModal
              onClose={() => setShowAddJournalModal(false)}
              onSubmit={handleAddJournal}
            />
          )}

          {showEditModal && editingTransaction && (
            <EditJournalModal 
              onClose={() => setShowEditModal(false)} 
              onSubmit={handleEditSubmit}
              transaction={editingTransaction}
            />
          )}

          {showDeleteModal && deletingTransaction && (
            <DeleteTransactionModal
              onClose={() => setShowDeleteModal(false)}
              onConfirm={handleConfirmDelete}
              transaction={deletingTransaction}
            />
          )}

          {showDatePicker && (
            <div ref={datePickerRef} className="financial-date-picker-anchor">
              <FinancialDateRangePicker
                currentMonth={currentMonth}
                startDate={selectedStartDate}
                endDate={selectedEndDate}
                onMonthChange={navigateMonth}
                onDateClick={handleDateClick}
                onApply={handleApplyDatePicker}
                onClear={handleClearDatePicker}
                onClose={() => setShowDatePicker(false)}
              />
            </div>
          )}

          <div className="financial-overview-section-header">
            <h2>All Transaction</h2>
            <div className="financial-overview-toolbar">
              <div className="financial-overview-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by transaction id or amount..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>

              <div className="journal-export-dropdown" ref={exportDropdownRef}>
                <button
                  type="button"
                  className={
                    isPartsURL
                      ? "journal-export-button-parts"
                      : isAccessoriesURL
                        ? "journal-export-button-acc"
                        : "journal-export-button"
                  }
                  onClick={() => setShowExportDropdown((value) => !value)}
                >
                  <Download size={16} />
                  Export
                  <ChevronDown
                    size={12}
                    className={`journal-export-dropdown-arrow ${
                      showExportDropdown ? "rotated" : ""
                    }`}
                  />
                </button>

                <div
                  className={`journal-export-dropdown-content ${
                    showExportDropdown ? "show" : ""
                  }`}
                >
                  {exportOptions.map((option) => (
                    <div
                      key={option.value}
                      className="journal-export-dropdown-item"
                      onClick={() => handleExportSelect(option.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          handleExportSelect(option.value);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <img
                        src={option.icon}
                        alt={option.label}
                        width="16"
                        height="16"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="financial-overview-table-wrap">
            <table className="financial-overview-table">
              <colgroup>
                <col className="financial-overview-col-date" />
                <col className="financial-overview-col-trn" />
                <col className="financial-overview-col-note" />
                <col className="financial-overview-col-amount" />
                <col className="financial-overview-col-performed" />
                <col className="financial-overview-col-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>TRN ID</th>
                  <th>Note</th>
                  <th>Amount</th>
                  <th>Performed By</th>
                  <th className="is-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="financial-overview-empty">Loading transactions...</td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="financial-overview-empty">No transactions found</td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td>{transaction.date.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      <td>{transaction.transactionId}</td>
                      <td>{transaction.note}</td>
                      <td className="journal-amount">
                        {transaction.tnx_type === "credit" ? (
                          <span className="journal-credit-amount">
                            -€{Math.abs(transaction.amount).toFixed(2)}
                          </span>
                        ) : (
                          <span className="journal-debit-amount">
                            +€{Math.abs(transaction.amount).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td>{transaction.performedBy}</td>
                      <td>
                        <div className="financial-overview-row-actions">
                          <button type="button" aria-label="Edit transaction" onClick={() => handleEditTransaction(transaction)}>
                            <EditIcon
                              fill={
                                isPartsURL
                                  ? "var(--primary-color-parts)"
                                  : isAccessoriesURL
                                    ? "var(--primary-color-acc)"
                                    : "var(--primary-color-mobile)"
                              }
                            />
                          </button>
                          <button type="button" aria-label="Delete transaction" onClick={() => handleDeleteTransaction(transaction)}>
                            <RedDeleteIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-wrapper">
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <div className="pagination-controls">
              <button
                type="button"
                className={`${isPartsURL ? "pagination-btn-parts" : isAccessoriesURL ? "pagination-btn-acc" : "pagination-btn"}`}
                disabled={currentPage === 1 || isLoading}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                className={`${isPartsURL ? "pagination-btn-next-parts" : isAccessoriesURL ? "pagination-btn-next-acc" : "pagination-btn-next"}`}
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default FinancialOverview;
